import { columnDefinitionKey, regularColumnsQuery } from './regularColumnsQuery';
import type { MarketField, StitchedColumnPlan } from './stitchedColumnPlan';
import { TableModelRowBackend } from './TableModelRowBackend';
import type { KeyedTableModel } from './TableModelRowBackend';
/**
 * Client-stitched columns for one keys-table grid (ADR 0020, Placement and ownership).
 *
 * Owns the grid's row membership (`rows`, passed to `TableModelGridSource`), a regular column group
 * over one consumer-local table, an optional market column group over an application-wide shared
 * provider, and the grid target. Without a market provider, the consumer-local table serves the
 * market columns too, so the widget holds two subscriptions (keys and columns) however many
 * symbols it shows (DEC-TABLES-001). The model that owns the keys table owns this too, so its lifetime follows
 * the table rather than the view: the view only binds its grid while mounted, and writes before
 * then are dropped like writes for rows the grid does not hold.
 *
 * Bindings follow the column plan. Showing, hiding, or adding a column changes demand only. A
 * changed definition under an existing column id, or changed column options, reopens the regular
 * group, because the consumer-local table must be redefined and its old values are not current
 * for the new definition (OD-RETAIN-001).
 */
import { effect, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { ColumnOptions } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { attachColumns } from '@thinkalpha/ui-keyed-columns/attachColumns';
import type { ColumnAttachment, ColumnBinding, ColumnProvider } from '@thinkalpha/ui-keyed-columns/contracts';
import { TableColumnProvider } from '@thinkalpha/ui-keyed-columns/tableColumnProvider';
import { WindowedKeySource } from '@thinkalpha/ui-keyed-columns/windowedKeySource';
import type { DeparturePolicy } from '@thinkalpha/ui-keyed-columns/windowedKeySource';
import { GridColumnTarget } from '@thinkalpha/ui-table/adapters/keyedColumnTarget';
import type { GridColumnTargetGrid } from '@thinkalpha/ui-table/adapters/keyedColumnTarget';
import type { GridRowFields, GridRowId } from '@thinkalpha/ui-table/types/row';
import type { TemplateColumn } from '@thinkalpha/ui-types/contracts/column-template';
import { isEqual } from 'es-toolkit';
import type { TableModelRowMembership } from 'src/features/table/next/tableModelGridSource';

export interface StitchedColumnsOptions {
    /** The current column plan; null while the keys table cannot be planned yet. */
    readonly plan: ReadonlySignal<StitchedColumnPlan | null>;
    /** The column options the keys table uses; regular-column tables use the same context. */
    readonly columnOptions: ReadonlySignal<Partial<ColumnOptions>>;
    /**
     * A shared market provider for the market columns, or null to serve them from the
     * consumer-local table with the regular columns.
     */
    readonly market: ColumnProvider<string, MarketField, unknown> | null;
    /** Creates the TableModel behind a consumer-local regular-column table. */
    readonly createTable: () => KeyedTableModel;
    /** How long window departures wait for the next keys batch (OD-SCROLL-001). */
    readonly departures: DeparturePolicy;
    readonly devtoolsName: string;
}

const symbolOf = (symbol: string | undefined): string | undefined => symbol;

/** What each regular column computes, by id, as compared for a redefinition. */
function definitions(columns: readonly TemplateColumn[]): Map<string, string> {
    return new Map(columns.map((column) => [column.id, columnDefinitionKey(column)]));
}

export class StitchedColumns implements Disposable {
    readonly #options: StitchedColumnsOptions;
    readonly #keys: WindowedKeySource<GridRowId, Readonly<GridRowFields>, string | undefined>;
    readonly #target: GridColumnTarget;
    readonly #market: ColumnAttachment<MarketField, string> | undefined;
    #regular: ColumnAttachment<string, string> | undefined;
    /** The backend behind `#regular`, whose open tables `tableKeys` reports. */
    #regularBackend: TableModelRowBackend | undefined;
    #regularSymbolColumn: TemplateColumn | undefined;
    #symbolFieldId: string | undefined;
    /** Regular columns the current group serves, by id; read by its table queries. */
    #regularColumns = new Map<string, TemplateColumn>();
    #regularDefinitions = new Map<string, string>();
    #regularColumnOptions: Partial<ColumnOptions> | undefined;
    #grid: GridColumnTargetGrid | null = null;
    readonly #lifetime = new DisposableStack();

    constructor(options: StitchedColumnsOptions) {
        this.#options = options;
        this.#keys = this.#lifetime.use(
            new WindowedKeySource<GridRowId, Readonly<GridRowFields>, string | undefined>({
                keyInput: (fields) => {
                    const symbol = this.#symbolFieldId === undefined ? undefined : fields[this.#symbolFieldId];
                    return typeof symbol === 'string' ? symbol : undefined;
                },
                departures: options.departures,
            }),
        );
        this.#target = new GridColumnTarget({
            applyRowField: (rowId, fieldId, value) => this.#grid?.applyRowField(rowId, fieldId, value) ?? false,
        });
        if (options.market !== null) {
            this.#market = this.#lifetime.use(
                attachColumns({
                    keys: this.#keys,
                    keyOf: symbolOf,
                    provider: options.market,
                    columns: [],
                    target: this.#target,
                }),
            );
        }
        this.#lifetime.defer(() => this.#regular?.[Symbol.dispose]());
        // Attachment work runs untracked: only the plan and the column options drive this effect.
        this.#lifetime.defer(
            effect(() => {
                const plan = options.plan.value;
                const columnOptions = options.columnOptions.value;
                untracked(() => this.#apply(plan, columnOptions));
            }),
        );
    }

    /**
     * Keys of the consumer-local tables serving the stitched columns, beside the keys table's own
     * key; empty until one attaches.
     */
    get tableKeys(): readonly string[] {
        return this.#regularBackend?.tableKeys ?? [];
    }

    /** Row membership for the keys table's grid source. */
    get rows(): TableModelRowMembership {
        return this.#keys;
    }

    /** Binds the mounted grid, or null on unmount. Stitched writes go to the bound grid only. */
    bindGrid(grid: GridColumnTargetGrid | null): void {
        this.#grid = grid;
    }

    /** Field writes the grid dropped because it did not hold the row, or none was bound. */
    get droppedWrites(): number {
        return this.#target.droppedWrites;
    }

    [Symbol.dispose](): void {
        this.#lifetime.dispose();
    }

    #apply(plan: StitchedColumnPlan | null, columnOptions: Partial<ColumnOptions>): void {
        this.#symbolFieldId = plan?.symbolFieldId;
        const marketColumns = plan?.marketColumns ?? [];
        this.#market?.replaceColumns(marketColumns.map(({ field, column }) => ({ field, column: column.id })));

        const regularColumns =
            this.#market === undefined
                ? [...(plan?.regularColumns ?? []), ...marketColumns.map(({ column }) => column)]
                : (plan?.regularColumns ?? []);
        const nextDefinitions = definitions(regularColumns);
        const symbolColumn = plan?.symbolColumn;
        const redefined =
            (symbolColumn === undefined) !== (this.#regularSymbolColumn === undefined) ||
            (symbolColumn !== undefined &&
                this.#regularSymbolColumn !== undefined &&
                columnDefinitionKey(symbolColumn) !== columnDefinitionKey(this.#regularSymbolColumn)) ||
            !isEqual(columnOptions, this.#regularColumnOptions) ||
            nextDefinitions.entries().some(([id, definition]) => {
                const previous = this.#regularDefinitions.get(id);
                return previous !== undefined && previous !== definition;
            });
        this.#regularColumns = new Map(regularColumns.map((column) => [column.id, column]));
        this.#regularDefinitions = nextDefinitions;
        this.#regularColumnOptions = columnOptions;
        this.#regularSymbolColumn = symbolColumn;
        const bindings: ColumnBinding<string, string>[] = regularColumns.map((column) => ({
            field: column.id,
            column: column.id,
        }));
        if (this.#regular !== undefined && !redefined) {
            this.#regular.replaceColumns(bindings);
            return;
        }
        this.#regular?.[Symbol.dispose]();
        const backend = symbolColumn === undefined ? undefined : this.#regularTables(symbolColumn, columnOptions);
        this.#regular =
            backend === undefined
                ? undefined
                : attachColumns({
                      keys: this.#keys,
                      keyOf: symbolOf,
                      provider: new TableColumnProvider(backend),
                      columns: bindings,
                      target: this.#target,
                  });
        this.#regularBackend = backend;
    }

    /** The tables for one regular group: one consumer-local table under one definition context. */
    #regularTables(symbolColumn: TemplateColumn, columnOptions: Partial<ColumnOptions>): TableModelRowBackend {
        return new TableModelRowBackend({
            createTable: this.#options.createTable,
            query: (request, userViewKey) =>
                regularColumnsQuery({
                    request,
                    userViewKey,
                    columns: this.#regularColumns,
                    symbolColumn,
                    columnOptions,
                }),
            symbolFieldId: symbolColumn.id,
            devtoolsName: `${this.#options.devtoolsName}/columns`,
        });
    }
}
